import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BrandMark } from '@/components/BrandMark';
import { toast } from 'sonner';
import { Sun, Moon, Mail, Lock, ShieldCheck } from 'lucide-react';

export default function LoginPage() {
  const { login, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSending, setForgotSending] = useState(false);

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotSending(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      toast.success('If that email is registered, a reset link is on its way.');
      setForgotMode(false);
      setForgotEmail('');
    } catch {
      toast.success('If that email is registered, a reset link is on its way.');
      setForgotMode(false);
    } finally {
      setForgotSending(false);
    }
  };

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success('Welcome back');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#6B1F2E] text-[#FAFAF7] p-12 flex-col justify-between relative overflow-hidden">
        {/* Background pattern */}
        <div className="brand-pattern absolute inset-0 opacity-25" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-[#8B3A47]/35" />
        
        <div className="relative z-10">
          <BrandMark inverse />
        </div>
        
        <div className="relative z-10 space-y-10">
          <div>
            <p className="eyebrow mb-5 text-[#C9A961]">Technology for saving lives</p>
            <h1 className="font-heading text-6xl font-medium leading-[1.02] mb-6">
              Calm command.<br />Fast response.
            </h1>
            <p className="text-[#FAFAF7]/78 text-lg leading-8 max-w-md">
              A composed emergency response workspace for Uber drivers and riders across Lagos and Abuja.
            </p>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-2xl border border-[#FAFAF7]/15 bg-[#FAFAF7]/8 p-5">
              <div className="text-4xl font-heading font-medium">1,600+</div>
              <div className="text-[#FAFAF7]/68 text-sm mt-2">Incidents handled</div>
            </div>
            <div className="rounded-2xl border border-[#FAFAF7]/15 bg-[#FAFAF7]/8 p-5">
              <div className="text-4xl font-heading font-medium">&lt;2m</div>
              <div className="text-[#FAFAF7]/68 text-sm mt-2">Average response</div>
            </div>
            <div className="rounded-2xl border border-[#FAFAF7]/15 bg-[#FAFAF7]/8 p-5">
              <div className="text-4xl font-heading font-medium">24/7</div>
              <div className="text-[#FAFAF7]/68 text-sm mt-2">Coverage</div>
            </div>
          </div>
        </div>
        

      </div>

      {/* Right side - Auth forms */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Theme toggle */}
          <div className="flex justify-end mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>

          {/* Mobile logo */}
          <div className="lg:hidden mb-8 flex justify-center">
            <BrandMark />
          </div>

          <Card>
            <CardHeader className="text-center pb-4">
              <CardTitle>Sign in</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@sety.io"
                      className="pl-10"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm(prev => ({ ...prev, email: e.target.value }))}
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="********"
                      className="pl-10"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                      required
                      data-testid="login-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
                <button
                  type="button"
                  onClick={() => { setForgotEmail(loginForm.email); setForgotMode(true); }}
                  className="w-full text-center text-sm text-muted-foreground hover:text-primary hover:underline"
                >
                  Forgot password?
                </button>
              </form>

              {forgotMode && (
                <form onSubmit={handleForgot} className="mt-4 space-y-3 rounded-lg border p-3">
                  <div className="text-sm font-medium">Reset your password</div>
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">Account email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="forgot-email"
                        type="email"
                        className="pl-10"
                        placeholder="you@example.com"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1" disabled={forgotSending}>
                      {forgotSending ? 'Sending...' : 'Send reset link'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setForgotMode(false)}>Cancel</Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>

          <div className="mt-6 flex items-center justify-center gap-2 text-muted-foreground text-sm">
            <ShieldCheck className="h-4.5 w-4.5 text-[#8B3A47]" />
            <span>
              Powered by{' '}
              <a
                href="https://thcohq.com"
                target="_blank"
                rel="noreferrer"
                className="text-[#8B3A47] font-semibold hover:underline underline-offset-4"
              >
                THCO
              </a>
              .
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
